import moment, {Moment, MomentInput, unitOfTime} from 'moment';

import {CALENDAR_DATE_FORMAT} from 'common/constants';
import {assertIsStandardTimeUnit, getCurrentDate} from 'common/util/date';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
} from 'common/widgets/date-range-picker/models/presets';

import {BaseDate, DateRangeAttributes, DateRangeType, RelativeDate, StandardTimeUnit, TimeUnit} from './types';

export const NUMBER_OF_DAYS_IN_UNIT: {
  [key in StandardTimeUnit]: number;
} = {
  [TimeUnit.Second]: 1 / 86400,
  [TimeUnit.Minute]: 1 / 1440,
  [TimeUnit.Hour]: 1 / 24,
  [TimeUnit.Day]: 1,
  [TimeUnit.Week]: 7,
  [TimeUnit.Month]: 30,
  [TimeUnit.Quarter]: 90,
  [TimeUnit.Year]: 365,
};

export function getNumberOfDays(dateRange: DateRangeAttributes, asOfDate: Moment): number {
  const fromDate = convertBaseDateToCalendarDate(dateRange.from);
  const toDate = convertBaseDateToCalendarDate(dateRange.to);
  switch (dateRange.type) {
    case DateRangeType.RelativeAfter:
      if (!dateRange.window) {
        throw new Error(`window required for RelativeAfter date ranges.`);
      }
      return Math.ceil(dateRange.window.value * NUMBER_OF_DAYS_IN_UNIT[dateRange.window.unit as StandardTimeUnit]);
    case DateRangeType.Since:
      return asOfDate.diff(fromDate as MomentInput, `days` as unitOfTime.Diff);
    case DateRangeType.On:
      return 1;
    case DateRangeType.Between:
      if (dateRange.from === START_OF_TODAY && dateRange.to === END_OF_TODAY) {
        return 1;
      }
      if (dateRange.from === START_OF_YESTERDAY && dateRange.to === END_OF_YESTERDAY) {
        return 1;
      }
      return moment(toDate as MomentInput).diff(fromDate as MomentInput, `days` as unitOfTime.Diff);
    default:
      throw new Error(`Not implemented for type ${dateRange.type}`);
  }
}

export function convertBaseDateToCalendarDate(baseDate?: BaseDate | null): string | null {
  if (!baseDate) {
    return null;
  }
  if (isRelativeDate(baseDate)) {
    return convertRelativeDateToCalendarDate(baseDate);
  }
  return baseDate;
}

export function isRelativeDate(date: BaseDate): date is RelativeDate {
  return Object.prototype.hasOwnProperty.call(date ?? {}, `unit`);
}

export function convertRelativeDateToCalendarDate(relativeDate: RelativeDate): string | null {
  return convertRelativeDateToCalendarDateAsOf(relativeDate, getCurrentDate());
}

export function convertRelativeDateToCalendarDateAsOf(
  relativeDate: RelativeDate,
  asOfDate: Date | Moment,
  dateFormat?: string,
): string | null {
  if (
    !relativeDate ||
    !(
      [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Day, TimeUnit.Week, TimeUnit.Month, TimeUnit.Year] as TimeUnit[]
    ).includes(relativeDate.unit)
  ) {
    return null;
  }

  /**
   * query-api considers 1 month = 30 days and 1 year = 365 days, so we parse dates to make labels, tooltips and other UI elements consistent.
   */
  if (relativeDate.unit === TimeUnit.Month) {
    relativeDate = {
      unit: TimeUnit.Day,
      value: relativeDate.value * 30,
    };
  } else if (relativeDate.unit === TimeUnit.Year) {
    relativeDate = {
      unit: TimeUnit.Day,
      value: relativeDate.value * 365,
    };
  }

  assertIsStandardTimeUnit(relativeDate.unit);
  return moment(asOfDate)
    .subtract(relativeDate.value, relativeDate.unit)
    .format(dateFormat || CALENDAR_DATE_FORMAT);
}
